import { z } from 'zod'

// Common validation patterns
export const emailSchema = z.string().email('Invalid email format')
export const passwordSchema = z.string().min(6, 'Password must be at least 6 characters')
export const requiredStringSchema = z.string().min(1, 'This field is required')
export const positiveNumberSchema = z.number().min(0, 'Must be a positive number')
export const priceSchema = z.coerce.number().min(0.01, 'Price must be greater than 0')

// User/Staff related schemas
export const userRoles = ['admin', 'manager', 'counter'] as const
export const userRoleSchema = z.enum(userRoles)

export const userStatusValues = ['active', 'inactive'] as const
export const userStatusSchema = z.enum(userStatusValues)

export const createUserSchema = z.object({
  name: requiredStringSchema.min(3, 'Name must be at least 3 characters'),
  email: emailSchema,
  password: passwordSchema,
  role: userRoleSchema,
  status: userStatusSchema.default('active'),
})

export const updateUserSchema = z.object({
  id: z.string().or(z.number()),
  name: requiredStringSchema.min(3, 'Name must be at least 3 characters').optional(),
  email: emailSchema.optional(),
  password: z.string().min(6, 'Password must be at least 6 characters').or(z.literal('')).optional(),
  role: userRoleSchema.optional(),
  status: userStatusSchema.optional(),
})

// Product related schemas
export const createProductSchema = z.object({
  name: requiredStringSchema.min(2, 'Product name must be at least 2 characters'),
  description: z.string().optional(),
  price: priceSchema,
  category_id: z.string().optional(),
  // image_url may be omitted, empty, a valid URL, or a data URL (uploaded image)
  image_url: z
    .union([
      z.string().url(),
      z.string().regex(/^data:.+;base64,.+$/),
      z.literal(''),
    ])
    .optional(),
  is_available: z.boolean().default(true),
  ingredients: z
    .array(
      z.object({
        ingredient_id: z.string(),
        quantity_required: z.number().min(0, 'Quantity must be at least 0'),
      })
    )
    .optional(),
})

export const updateProductSchema = createProductSchema.partial().extend({
  id: z.string().or(z.number()),
})

// Category related schemas
export const createCategorySchema = z.object({
  name: requiredStringSchema.min(2, 'Category name must be at least 2 characters'),
  description: z.string().optional(),
  image_url: z.string().url().optional().or(z.literal('')),
})

export const updateCategorySchema = createCategorySchema.partial().extend({
  id: z.string().or(z.number()),
})

// Table related schemas
export const tableStatusValues = ['available', 'occupied'] as const
export const tableStatusSchema = z.enum(tableStatusValues)

export const createTableSchema = z.object({
  table_number: requiredStringSchema.min(1, 'Table number is required'),
  capacity: z.number().min(1, 'Table must have at least 1 seat').max(20, 'Maximum 20 seats per table'),
  status: tableStatusSchema.default('available'),
})

export const updateTableSchema = createTableSchema.partial().extend({
  id: z.string().or(z.number()),
})

// Order related schemas
export const orderTypeValues = ['dine_in', 'takeout', 'delivery'] as const
export const orderTypeSchema = z.enum(orderTypeValues)

export const orderStatusValues = ['confirmed', 'preparing', 'served', 'completed', 'cancelled'] as const
export const orderStatusSchema = z.enum(orderStatusValues)

export const orderItemSchema = z.object({
  product_id: z.number(),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  notes: z.string().optional(),
})

export const createOrderSchema = z.object({
  table_id: z.number().optional(),
  customer_name: z.string().optional(),
  order_type: orderTypeSchema,
  notes: z.string().optional(),
  items: z.array(orderItemSchema).min(1, 'Order must have at least one item'),
})

// Settings schemas
export const posSettingsSchema = z.object({
  restaurant_name: requiredStringSchema,
  address: z.string().optional(),
  phone: z.string().optional(),
  email: emailSchema.optional(),
  tax_rate: z.number().min(0).max(1), // 0.08 for 8%
  currency_symbol: requiredStringSchema.default('Rs'),
  receipt_footer: z.string().optional(),
  auto_print_receipts: z.boolean().default(false),
  order_timeout_minutes: z.number().min(1).max(120).default(30),
})

// Login schema
export const loginSchema = z.object({
  username: requiredStringSchema,
  password: requiredStringSchema,
})

// Expense related schemas
export const expenseStatusValues = ['pending', 'approved', 'rejected'] as const
export const expenseStatusSchema = z.enum(expenseStatusValues)

export const expenseCategoryValues = [
  'utilities',
  'rent',
  'salaries',
  'supplies',
  'maintenance',
  'marketing',
  'transportation',
  'equipment',
  'food_ingredients',
  'other'
] as const
export const expenseCategorySchema = z.enum(expenseCategoryValues)

export const createExpenseSchema = z.object({
  title: requiredStringSchema.min(3, 'Title must be at least 3 characters'),
  description: requiredStringSchema.min(3, 'Description must be at least 3 characters'),
  amount: priceSchema,
  category: expenseCategorySchema,
  expense_date: requiredStringSchema,
  receipt_url: z.string().url().optional().or(z.literal('')),
  notes: z.string().optional(),
})

export const updateExpenseSchema = createExpenseSchema.partial().extend({
  id: z.string().or(z.number()),
})

export const approveExpenseSchema = z.object({
  notes: z.string().optional(),
})

export const rejectExpenseSchema = z.object({
  rejection_reason: requiredStringSchema.min(3, 'Rejection reason must be at least 3 characters'),
})

// Export types
export type CreateUserData = z.infer<typeof createUserSchema>
export type UpdateUserData = z.infer<typeof updateUserSchema>
export type CreateProductData = z.infer<typeof createProductSchema>
export type UpdateProductData = z.infer<typeof updateProductSchema>
export type CreateCategoryData = z.infer<typeof createCategorySchema>
export type UpdateCategoryData = z.infer<typeof updateCategorySchema>
export type CreateTableData = z.infer<typeof createTableSchema>
export type UpdateTableData = z.infer<typeof updateTableSchema>
export type CreateOrderData = z.infer<typeof createOrderSchema>
export type LoginData = z.infer<typeof loginSchema>
export type POSSettingsData = z.infer<typeof posSettingsSchema>
export type CreateExpenseData = z.infer<typeof createExpenseSchema>
export type UpdateExpenseData = z.infer<typeof updateExpenseSchema>
export type ApproveExpenseData = z.infer<typeof approveExpenseSchema>
export type RejectExpenseData = z.infer<typeof rejectExpenseSchema>

